import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Box, IconButton, ListItem } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Refresh';

import { getClientCode } from 'reducers/Login';
import { setCloudStatus, syncLocalDatabaseSO } from 'actions/connectivity';
import { resetDB } from 'services/DB';
import { stopSync } from 'services/DB/syncDB';
import { SyncableSOName } from 'services/DB/types';

import { CloudStatus } from './types';
import ProgressBar from './ProgressBar';

function refreshTable(name: SyncableSOName) {
  return async dispatch => {
    await dispatch(setCloudStatus(name, { done: 0, progress: 0, todo: 0 }));
    await stopSync(name);
    dispatch(syncLocalDatabaseSO(name));
  };
}

function resetTable(name: SyncableSOName, clientCode: string) {
  return async dispatch => {
    await dispatch(
      setCloudStatus(name, {
        done: 0,
        progress: 0,
        todo: 0,
        syncing: true,
      }),
    );
    await stopSync(name);
    await resetDB(clientCode, name);
    dispatch(syncLocalDatabaseSO(name));
  };
}

function LoadingCloudSync({
  todo,
  progress,
  done,
  name,
}: Pick<CloudStatus, 'todo' | 'progress' | 'done' | 'name'>) {
  const { t } = useTranslation('header');
  return todo || progress || done ? (
    <Box
      data-testid="syncing-block"
      data-test-key={name}
      style={{ textAlign: 'center', width: '100%' }}
    >
      <Trans
        i18nKey="header:sync.database.processing"
        tOptions={{ done, progress, todo }}
      >
        <strong />
      </Trans>
      <ProgressBar
        done={done}
        step={progress}
        total={done + progress + todo}
        dataTestId="progress-bar"
        dataTestKey={`progress-bar-${name}`}
      />
    </Box>
  ) : (
    <div>
      <strong data-testid="waiting-sync" data-test-key={`waiting-sync-${name}`}>
        {t('sync.database.waiting')}
      </strong>
    </div>
  );
}

function LoadedCloudSync({
  name,
  time,
  done,
}: Pick<CloudStatus, 'name' | 'time' | 'done'>) {
  const { t } = useTranslation('header');
  return done ? (
    <>
      <div data-testid="synced-count" data-test-key={`synced-count-${name}`}>
        {t('sync.database.done1', { done })}
      </div>
      <div data-testid="done-syncing" data-test-key={`done-syncing-${name}`}>
        {t('sync.database.done2', {
          time: time.toLocaleTimeString(),
        })}
      </div>
    </>
  ) : (
    <div>
      <strong data-testid="empty-sync" data-test-key={`empty-sync-${name}`}>
        {t('sync.database.empty')}
      </strong>
    </div>
  );
}

function CloudSync({
  name,
  done,
  progress,
  todo,
  time,
  syncing,
}: Pick<CloudStatus, 'done' | 'progress' | 'todo' | 'time' | 'syncing'> & {
  name: SyncableSOName;
}) {
  const { t } = useTranslation('header');
  const clientCode = useSelector(getClientCode);
  const dispatch = useDispatch();

  return (
    <ListItem
      divider
      key={name}
      data-testid="cloudsync-item"
      data-test-key={name}
      style={{ flexDirection: 'column', minWidth: 'min(345px, 80vw)' }}
    >
      <strong>{name}</strong>
      {syncing ? (
        <LoadingCloudSync
          todo={todo}
          progress={progress}
          done={done}
          name={name}
        />
      ) : (
        <LoadedCloudSync name={name} time={time} done={done} />
      )}
      <Box style={{ justifyContent: 'center' }}>
        <IconButton
          size="small"
          data-testid="icon-refresh"
          data-test-key={`icon-refresh-${name}`}
          onClick={e => {
            e.stopPropagation();
            return dispatch(refreshTable(name));
          }}
          style={{ margin: '0 0.5rem' }}
          title={t('sync.database.refreshTitle')}
        >
          <RefreshIcon />
        </IconButton>

        <IconButton
          data-testid="icon-trash"
          data-test-key={`icon-trash-${name}`}
          size="small"
          onClick={e => {
            e.stopPropagation();
            return dispatch(resetTable(name, clientCode));
          }}
          style={{ margin: '0 0.5rem' }}
          title={t('sync.database.trashTitle')}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </ListItem>
  );
}

export default CloudSync;
